<template>
    <div :style="options.container" class="media-carousel">
        <div :style="carouselStyles" class="carousel">
            <div v-for="(fileObj, indx) in mediaList" :key="indx" :class="mediaClass(indx)">
                <video
                    v-if="isVideo(fileObj)"
                    :src="previewSrc(fileObj)"
                    :style="optionalMediaStyles(indx)"
                    @click="handleMediaClick(indx)"
                ></video>
                <img
                    v-else
                    :src="previewSrc(fileObj)"
                    :style="optionalMediaStyles(indx)"
                    @click="handleMediaClick(indx)"
                    :alt="fileObj.name"
                />
            </div>
        </div>
        <div v-if="!screen.isMobile.value" class="carousel-action">
            <button
                :class="nextBtnClass"
                :style="optionalBtnStyles('next')"
                @click="handleNextMedia"
            ></button>
            <button
                :class="prevBtnClass"
                :style="optionalBtnStyles('prev')"
                @click="handlePrevMedia"
            ></button>
        </div>
        <div class="gallery" v-if="!screen.isMobile.value">
            <slot></slot>
        </div>
    </div>
</template>
<script>
    import useCheckScreen from '@/composables/useCheckScreen.js'
    export default {
        name: 'Carousel',
        props: {
            mediaList: {
                type: Array,
                required: true,
                default: () => [],

                validator: (val) => {
                    if (Array.isArray(val)) {
                        if (val.length === 0) {
                            return true
                        } else {
                            return val.every((i) => typeof i === 'object' || i instanceof File)
                        }
                    }

                    return false
                }
            },

            options: {
                type: Object,
                required: false,
                default: () => ({
                    currentMedia: {
                        width: 376,
                        height: 387
                    },

                    otherMedia: {
                        width: 302,
                        height: 312
                    },

                    actionBtns: {
                        Xoffset: 275,
                        Yoffset: 225,
                        twoMediaXoffSet: 120
                    }
                }),

                validator: (val) => {
                    if (
                        typeof val === 'object' &&
                        'currentMedia' in val &&
                        'otherMedia' in val &&
                        'actionBtns' in val
                    ) {
                        return true
                    }

                    return false
                }
            }
        },

        data: () => ({
            mediaIndx: 0
        }),

        methods: {
            isCurrentMedia(indx) {
                return indx === this.mediaIndx
            },

            isNextMedia(indx) {
                let currListLen = this.mediaList.length

                if (indx === this.mediaIndx + 1) return true
                if (this.mediaIndx === currListLen - 1 && indx === 0) return true

                return indx === this.mediaIndx + 1
            },

            isPrevMedia(indx) {
                let currListLen = this.mediaList.length

                if (indx === this.mediaIndx - 1) return true
                if (indx === currListLen - 1 && this.mediaIndx === 0) return true

                return false
            },

            isHiddenMedia(indx) {
                let currListLen = this.mediaList.length

                if (currListLen <= 3) return false

                if (
                    this.isNextMedia(indx) ||
                    this.isPrevMedia(indx) ||
                    this.isCurrentMedia(indx)
                )
                    return false

                return true
            },

            handleNextMedia() {
                if (this.mediaList.length < 1) return

                if (this.mediaIndx >= this.mediaList.length - 1) {
                    this.mediaIndx = 0
                } else {
                    this.mediaIndx = this.mediaIndx + 1
                }

                this.$emit('currentMediaIndx', this.mediaIndx)
            },

            handlePrevMedia() {
                if (this.mediaList.length < 1) return

                if (this.mediaIndx <= 0) {
                    this.mediaIndx = this.mediaList.length - 1
                } else {
                    this.mediaIndx = this.mediaIndx - 1
                }

                this.$emit('currentMediaIndx', this.mediaIndx)
            },

            handleMediaClick(indx) {
                if (this.isPrevMedia(indx) && this.screen.isMobile.value) {
                    this.handlePrevMedia()
                } else if (this.isNextMedia(indx) && this.screen.isMobile.value) {
                    this.handleNextMedia()
                } else {
                    return
                }
            }
        },

        computed: {
            previewSrc: () => (fileObj) => {
                if (fileObj instanceof File) {
                    return URL.createObjectURL(fileObj)
                } else {
                    return fileObj.src
                }
            },
            isVideo: () => (fileObj) => fileObj.type.includes('video'),

            mediaClass: (self) => (indx) => {
                if (self.isHiddenMedia(indx)) return { 'hidden-media': true }
                if (self.isCurrentMedia(indx)) return { 'curr-media': true }
                if (self.isNextMedia(indx)) return { 'next-media': true }
                if (self.isPrevMedia(indx)) return { 'prev-media': true }

                return { 'hidden-media': true }
            },

            nextBtnClass: (self) => {
                if (self.mediaList.length === 1) {
                    return { 'hide-btn': true }
                } else {
                    return { 'next-media-btn': true }
                }
            },

            prevBtnClass: (self) => {
                if (self.mediaList.length < 3) {
                    return { 'hide-btn': true }
                } else {
                    return { 'prev-media-btn': true }
                }
            },

            optionalMediaStyles: (self) => (indx) => {
                if (self.isHiddenMedia(indx)) return {}
                if (self.isCurrentMedia(indx))
                    return {
                        width: `${self.options.currentMedia.width}px`,
                        height: `${self.options.currentMedia.height}px`
                    }
                if (self.isNextMedia(indx) || self.isPrevMedia(indx))
                    return {
                        width: `${self.options.otherMedia.width}px`,
                        height: `${self.options.otherMedia.height}px`
                    }
            },

            optionalBtnStyles: (self) => (btn) => {
                if (btn === 'next') {
                    if (self.mediaList.length === 2) {
                        return {
                            left: `${self.options.actionBtns.twoMediaXoffSet}px`,
                            bottom: `${self.options.actionBtns.Yoffset}px`
                        }
                    }
                    return {
                        left: `${self.options.actionBtns.Xoffset}px`,
                        bottom: `${self.options.actionBtns.Yoffset}px`
                    }
                } else {
                    return {
                        right: `${self.options.actionBtns.Xoffset}px`,
                        bottom: `${self.options.actionBtns.Yoffset}px`
                    }
                }
            },

            carouselStyles: (self) =>
                self.mediaList.length === 2 ? { 'margin-left': 'auto' } : {}
        },

        watch: {
            mediaList: function () {
                const mediaLen = this.mediaList.length ?? 0

                this.mediaIndx = mediaLen === 0 ? 0 : mediaLen - 1

                this.$emit('currentMediaIndx', this.mediaIndx)
            }
        },
        setup() {
            const { isMobile } = useCheckScreen()

            return {
                screen: {
                    isMobile
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
    $mobileMax: 600px;

    @media screen and (max-width: $mobileMax) {
        .carousel {
            max-width: 100px !important;
        }
    }

    .media-carousel {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .carousel {
            flex: 0 0;
            order: 1;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            max-width: 480px;

            .prev-media {
                order: 1;
                flex: 0 0 auto;
                transform: translateX(70%);
                img,
                video {
                    opacity: 0.5;
                    border-radius: 15px;
                    width: 302px;
                    height: 312px;
                    object-fit: cover;
                }
            }

            .curr-media {
                order: 2;
                flex: 0 0 auto;
                z-index: 1;
                img,
                video {
                    box-shadow: 0px 0px 70px 30px #121212;
                    border-radius: 15px;
                    width: 376px;
                    height: 387px;
                    object-fit: cover;
                }
            }

            .next-media {
                order: 3;
                flex: 0 0 auto;
                transform: translateX(-70%);

                img,
                video {
                    opacity: 0.5;
                    border-radius: 15px;
                    width: 302px;
                    height: 312px;
                    object-fit: cover;
                }
            }

            .hidden-media {
                display: none;
            }
        }

        .carousel-action {
            flex: 0 0 auto;
            order: 2;
            height: 0px;
            z-index: 1;

            button {
                width: 24px;
                height: 24px;
                position: relative;
                bottom: 225px;
                border: none;
                background-size: 24px 24px;
            }

            .next-media-btn {
                background: url(/images/icons/carrot-right.svg);
            }

            .prev-media-btn {
                background: url(/images/icons/carrot-left.svg);
            }

            .hide-btn {
                display: none;
            }
        }
        .gallery {
            order: 3;
        }

        .title {
            flex: 0 0;
            order: 3;
        }
    }
</style>
